import { sessionIdentity } from './offlineSession';
/** A shared source is a client playback key, never a legacy Render or an approval.
 * Existing player/session fields retain their names for persisted compatibility.
 * These keys never go to /renders, legacy progress, metrics or offline caches.
 */
import { BedtimePair } from '../context/bedtime/bedtimeContextModel';
import { sharedReviewEnabled } from '../audio/personalMessagesApi';
import { RenderDetail, RenderListItem, fetchRender, fetchRenders } from './rendersApi';
import { approvedMessages, Catalog, exactSharedAudio, HouseholdStory, preparePlayback, sharedCatalog, sharedStatus, SharedStatus } from './sharedHouseholdApi';

// One bounded in-memory download; no durable/offline authorization is implied.
let cached: { key: string; user: string; path: string; hash: string; pins: string; blob: Blob } | null = null;
let accessEpoch = 0;
export function clearSharedPlaybackCache(): void { cached = null; accessEpoch += 1; }
if (typeof window !== 'undefined') {
  window.addEventListener('offline', clearSharedPlaybackCache);
  window.addEventListener('luma-access-invalidated', clearSharedPlaybackCache);
}
const PREFIX = 'shared-story:';
interface Source extends BedtimePair { candidate: string; story: string; kind?: 'legacy' }
export function isSharedPlayback(key: string): boolean { return key.startsWith(PREFIX); }
export function sharedPlaybackKey(story: HouseholdStory, pair: BedtimePair): string {
  if (!story.candidate_id) throw new Error('Story not ready');
  return PREFIX + encodeURIComponent(JSON.stringify({ householdId: pair.householdId, listenerId: pair.listenerId, voiceId: pair.voiceId, candidate: story.candidate_id, story: story.story_id, kind: story.source_kind }));
}
function source(key: string): Source {
  const row = JSON.parse(decodeURIComponent(key.slice(PREFIX.length))) as Source;
  if (!row.householdId || !row.listenerId || !row.voiceId || !row.candidate || !row.story || (row.kind != null && row.kind !== 'legacy')) throw new Error('Story unavailable');
  return row;
}
export function sharedLibraryRows(catalog: Catalog, pair: BedtimePair): RenderListItem[] {
  if (!approvedMessages(catalog.messages)) return [];
  return catalog.stories.filter(story => story.candidate_id && story.narration_approved && story.funding_ready !== false).map(story => ({
    render_id: sharedPlaybackKey(story, pair), story_id: story.story_id, household_id: pair.householdId,
    listener_id: pair.listenerId, voice_id: pair.voiceId, listener_name: null, child_slug: null,
    status: 'parent_approved', created_at: '1970-01-01T00:00:00Z',
  }));
}
export async function fetchLibrary(pair: BedtimePair, signal?: AbortSignal): Promise<RenderListItem[]> {
  const [renders, catalog] = await Promise.all([
    fetchRenders({status: 'parent_approved', ...{householdId: pair.householdId, listenerId: pair.listenerId, voiceId: pair.voiceId}, signal}),
    sharedReviewEnabled && navigator.onLine !== false ? sharedCatalog(pair, signal) : Promise.resolve(null),
  ]);
  const exact = renders.filter(r => r.status === 'parent_approved' && r.household_id === pair.householdId && r.listener_id === pair.listenerId && r.voice_id === pair.voiceId);
  const shared = catalog ? sharedLibraryRows(catalog, pair) : [];
  // Preserve already-authorized exact-listener legacy playback during migration.
  // Only a fully eligible shared source replaces that existing approved master.
  // This never makes a legacy recording available to another listener.
  const sharedStories = new Set(shared.map(row => row.story_id));
  return [...exact.filter(row => !sharedStories.has(row.story_id)), ...shared];
}
export function storyDetailsPath(row: RenderListItem): string {
  return isSharedPlayback(row.render_id) ? `/household-stories?story=${encodeURIComponent(row.story_id)}` : `/renders/${row.render_id}`;
}
export function statusPins(status: SharedStatus): string {
  return JSON.stringify([status.audio_sha256, status.manifest_sha256, status.narration_approved, status.messages, status.funding_ready]);
}
function ready(status: SharedStatus): boolean {
  return status.status === 'ready' && status.narration_approved && approvedMessages(status.messages) && status.funding_ready !== false;
}
export interface LibraryDetail extends RenderDetail { sharedPins?: string }
export async function openLibraryAudio(key: string, signal?: AbortSignal): Promise<LibraryDetail> {
  if (!isSharedPlayback(key)) return fetchRender(key, true, signal);
  if (!sharedReviewEnabled || navigator.onLine === false) throw new Error('Connect to open this story');
  const epoch = accessEpoch;
  const user = sessionIdentity()?.userId;
  const ref = source(key);
  const catalog = await sharedCatalog(ref, signal);
  const match = catalog.stories.find(story => story.candidate_id === ref.candidate && story.story_id === ref.story && story.source_kind === ref.kind);
  if (!match || !sharedLibraryRows({...catalog, stories: [match]}, ref).some(row => row.render_id === key)) throw new Error('Story unavailable for this bedtime');
  const before = await sharedStatus(ref.candidate, ref.listenerId, signal, ref.kind);
  if (!ready(before)) throw new Error('Finish this story in your library');
  const playback = await preparePlayback(ref.candidate, ref.listenerId, signal, ref.kind);
  const reusable = user && cached && cached.key === key && cached.user === user && cached.path === playback.audio_url && cached.hash === playback.audio_sha256 && cached.pins === statusPins(before);
  const blob = reusable ? cached!.blob : await exactSharedAudio(playback.audio_url.replace(/^\/household-stories(?=\/)/, ''), playback.audio_sha256, signal);
  const after = await sharedStatus(ref.candidate, ref.listenerId, signal, ref.kind);
  if (signal?.aborted || sessionIdentity()?.userId !== user || epoch !== accessEpoch || !(navigator.onLine as boolean) || !ready(after) || statusPins(before) !== statusPins(after)) { clearSharedPlaybackCache(); throw new Error('Story changed'); }
  if (user) cached = {key, user, path: playback.audio_url, hash: playback.audio_sha256, pins: statusPins(after), blob};
  return {render_id: key, story_id: ref.story, story_title: match.title, household_id: ref.householdId, listener_id: ref.listenerId,
    voice_id: ref.voiceId, listener_name: null, child_slug: null, status: 'parent_approved', engine: null,
    audio_url: URL.createObjectURL(blob), sharedPins: statusPins(after), review_samples: [], segments: [], regens_today: 0, regen_limit: 0};
}
export function releaseLibraryAudio(detail: RenderDetail | null): void {
  if (detail && isSharedPlayback(detail.render_id) && detail.audio_url?.startsWith('blob:')) URL.revokeObjectURL(detail.audio_url);
}
export async function revalidateLibraryAudio(detail: LibraryDetail, signal?: AbortSignal): Promise<boolean> {
  if (!isSharedPlayback(detail.render_id)) return true;
  if (navigator.onLine === false) return false;
  const ref = source(detail.render_id);
  const status = await sharedStatus(ref.candidate, ref.listenerId, signal, ref.kind);
  return ready(status) && statusPins(status) === detail.sharedPins;
}
